import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { TypeKoodoGenericPageTemplateFields } from 'contentful-types'

import { getServerSideLocale } from '@telus/next-locale-utilities'
import koodoAnalyticsScripts from '@telus/koodo-analytics-scripts'

import { Language, Locale, Province, removeLocaleFromSlug } from '@/siteBuilder/locale'

import { getContentfulLocale } from '@/siteBuilder/locale/getContentfulLocale'
import { getGenericPageTemplateFromSlug } from '@/siteBuilder/utils/getGenericPageTemplateFromSlug'
import { KoodoGenericPage, KoodoGenericPageProps } from '@/siteBuilder/components/KoodoGenericPage'

import sitebuilderConfig from '@/sitebuilder.config'
import { ISiteBuilderEntry } from '@/siteBuilder/utils/types/ISiteBuilderEntryTypes'
import { ValidExcludes, handleExcludesQueryParam } from '../../utils/handleExcludesQueryParam'
import { promiseAllProps } from '../../utils/promiseAllProps'
import { AnalyticsScript } from '../../siteBuilder/lineOfBusiness/types'
import { handleRedirect } from '@/siteBuilder/utils/handleRedirect'
import { Viewport } from '@/siteBuilder/components/Theme/Theme'
import { resolveTimelinePreviewFromCtx, TimelinePreviewOption } from '../../siteBuilder/utils/toTimelinePreviewOption'
import { TimelinePreviewError } from '../../siteBuilder/components/TimelinePreviewError/TimelinePreviewError'

// Function to detect viewport from User-Agent (server-side estimation)
const getInitialViewportFromUserAgent = (userAgent?: string): Viewport => {
  if (!userAgent) return 'lg' // Default fallback

  // Mobile devices - typically smaller screens
  if (/Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return 'xs'
  }

  // Tablet devices - medium screens
  if (/iPad|Tablet/i.test(userAgent)) {
    return 'md'
  }

  // Desktop - assume large screen by default
  return 'xl'
}

async function getKoodoGPT(
  slug: string,
  ctx: GetServerSidePropsContext,
  locale: string,
  correlationId: string,
  timelinePreviewOption: TimelinePreviewOption | null
) {
  const forceContentfulFallbackCache = (ctx.req.headers['force-contentful-fallback-cache'] as string) || 'false'

  const genericPageTemplate = await getGenericPageTemplateFromSlug(
    slug === '' ? '/' : slug,
    sitebuilderConfig.space,
    ctx.req.cookies,
    locale,
    correlationId,
    forceContentfulFallbackCache,
    'koodoGenericPageTemplate',
    timelinePreviewOption
  )
  return genericPageTemplate as ISiteBuilderEntry<TypeKoodoGenericPageTemplateFields>
}

type KoodoPageProps = Omit<KoodoGenericPageProps, 'koodoGenericPageTemplate'> & {
  koodoGenericPageTemplate?: KoodoGenericPageProps['koodoGenericPageTemplate']
  timelinePreviewError?: boolean
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<KoodoPageProps>> {
  const { params } = ctx
  const slug = removeLocaleFromSlug(params?.slug)
  let correlationId = ''
  // correlation id is found in different places depending on if nextjs is run via npm run dev or npm start
  // Not sure why :(
  if (ctx.req.headers['correlation-id']) {
    // Required in dev
    correlationId = ctx.req.headers['correlation-id'] as string
  } else if (ctx.res?.getHeader('correlation-id')) {
    // Required in prod
    correlationId = ctx.res?.getHeader('correlation-id') as string
  }

  const serverSideLocale = getServerSideLocale(ctx)
  const locale: Locale = {
    language: serverSideLocale.lang as Language,
    province: serverSideLocale.prov as Province,
    country: 'CA',
  }

  const contentfulLocale = getContentfulLocale(locale)

  const timelinePreviewOption = resolveTimelinePreviewFromCtx(ctx, { correlationId, slug })

  const requests = {
    koodoGenericPageTemplate: getKoodoGPT(slug, ctx, contentfulLocale, correlationId, timelinePreviewOption),
  }

  let koodoGenericPageTemplate
  try {
    const result = await promiseAllProps(requests)
    koodoGenericPageTemplate = result.koodoGenericPageTemplate
  } catch (error) {
    if (timelinePreviewOption && process.env.APP_ENV !== 'production') {
      // Render the diagnostic page inline at the original URL. The APP_ENV check is defence in
      // depth — `timelinePreviewOption` is already null in production because resolveTimelinePreviewFromCtx
      // gates the whole timeline path off there — but the explicit guard here ensures end users
      // can never see this page if that upstream gate is ever refactored.
      return {
        props: {
          timelinePreviewError: true,
          locale,
          brand: 'koodo' as const,
          excludes: {},
          analyticsScripts: [],
        },
      }
    }
    return {
      notFound: true,
    }
  }

  // Handle redirect
  if (koodoGenericPageTemplate.fields.redirect) {
    return {
      redirect: handleRedirect(koodoGenericPageTemplate.fields.redirect.fields),
    }
  }

  // Check the exclude query param to see if we should exclude any functionality
  // Supported excludes are 'ge' and 'analytics'
  // They may be passed together as a comma separated list
  // Ex. ?exclude=ge,analytics
  let excludes: Partial<Record<ValidExcludes, boolean>> = null
  if (process.env.APP_ENV !== 'production') {
    excludes = handleExcludesQueryParam(ctx.query?.excludes as string)
  }

  let analyticsEnvironment: string
  switch (process.env.APP_ENV) {
    case 'production':
      analyticsEnvironment = 'production'
      break
    default:
      analyticsEnvironment = 'staging'
      break
  }

  const analyticsScript: AnalyticsScript = {
    src: koodoAnalyticsScripts.getTagManagerScriptPath(analyticsEnvironment, 'commerce') as string,
    defer: true,
  }

  // Detect initial viewport from User-Agent
  const userAgent = ctx.req.headers['user-agent']
  const initialViewport = getInitialViewportFromUserAgent(userAgent)

  return {
    props: {
      koodoGenericPageTemplate,
      locale,
      brand: 'koodo',
      excludes,
      analyticsScripts: [analyticsScript],
      initialViewport,
    },
  }
}

export default function KoodoPage(props: KoodoPageProps) {
  if (props.timelinePreviewError) {
    return <TimelinePreviewError />
  }
  if (!props.koodoGenericPageTemplate) {
    return null
  }
  return (
    <KoodoGenericPage
      koodoGenericPageTemplate={props.koodoGenericPageTemplate}
      locale={props.locale}
      brand="koodo"
      excludes={props.excludes}
      analyticsScripts={props.analyticsScripts}
    />
  )
}
